import React, { useState, useEffect, useRef } from 'react';
import { Checkbox, CircularProgress, IconButton } from '@mui/material';
import { Button } from '../../Button';
import clsx from 'clsx';
import { ButtonContainer, CoverTitle, FauxPage, PageBreak, PrintCopyright, SectionToggleLabel, SectionToggles, StyledDialog, Title } from './styles';
import { get, size, first, orderBy } from 'global/lib/lodash';
import { executePrint, getRealmName, isNativeAppBrowser, saveWorksheet } from '../../../utils';
import { useView } from '../../../hooks/useView';
import useBodyOverflow from 'global/hooks/useBodyOverflow';
import { useScreen } from 'global/styles';
import { getCopyrightText, useCopyrightHolder } from 'global/components/PageFooter';
import sendData from 'redux/actions/sendData';
import moment from 'moment';
import * as arrayToTree from 'array-to-tree';
import { getItemType, isGob } from '../../../../feature/report/AssessmentScore/utils';
import { NAV_LOCATION_BANNER_HEIGHT_VAR } from 'global/components/FlexibleLayout/constants';

const PrintRow = (props) => {
  const { li, first, pagebreak } = props;
  const itemPagebreak = get(li, 'pagebreak', pagebreak);
  // Per-section pagebreak wins; otherwise use PrintView default (break before each section).
  // pagebreak: false keeps the section on the same sheet as the prior one (e.g. score summary after info).
  const skipBreak = itemPagebreak === false || first;

  return (
    <>
      {!Object.keys(li).length ? (
        <></>
      ) : (
        <PageBreak className={clsx(skipBreak && 'first')}>
          {!!li.title && <div style={get(li, 'titleStyle', {})}>{li.title}</div>}
          <div style={li.style}>{li.render({ onLoaded: props.onLoaded })}</div>
        </PageBreak>
      )}
    </>
  );
};

export const testNativeSelect = () => {
  return new Promise((resolve, reject) => {
    if (isNativeAppBrowser()) {
      const link = window.location.origin + window.location.pathname;
      get(
        window,
        'TLM.nativeAlert',
        () => 0
      )({
        message: `This functionality is not available on the app. Please visit ${link} to use.`,
        link,
        buttons: [{ text: 'Okay' }, { text: 'Visit Link', action: 'link' }],
      });
      return reject();
    }

    resolve();
  });
};

const getLocationBannerOffset = () => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(NAV_LOCATION_BANNER_HEIGHT_VAR);
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getStickyRestTop = () => (window.matchMedia('(max-width:600px)').matches ? 65 : 75) + getLocationBannerOffset();

const getStickyScrollTop = () => Math.max(0, getStickyRestTop() - window.scrollY);

export const PrintView = (props) => {
  //items come over as { render: () => {}, style: {{}} }
  const { items, title, button, onClose, onOpen, assessment, pagebreak } = props;

  const { view, toggleTheme } = useView();
  const [open, setOpen] = useState(false);
  const [toggleBack, setToggleBack] = useState(false);
  const [loaded, setLoaded] = useState({});
  const { setBodyOverflow } = useBodyOverflow();
  const [exp, setExp] = useState({
    loading: false,
  });
  const stickyButtons = get(props, 'stickyButtons', false);
  const [stickyTop, setStickyTop] = useState(getStickyRestTop);
  // IconButtons below lg; labeled Buttons at lg+.
  const { isDown: isIconOnly } = useScreen('lg');
  const copyrightHolder = useCopyrightHolder({
    ownerRealmId: props.ownerRealmId,
    vendorId: props.vendorId != null ? props.vendorId : get(assessment, 'vendorId'),
  });

  useEffect(() => {
    if (!stickyButtons || open) {
      setStickyTop(getStickyRestTop());
      return;
    }

    const updateStickyTop = () => {
      setStickyTop(getStickyScrollTop());
    };

    window.addEventListener('scroll', updateStickyTop, { passive: true });
    window.addEventListener('resize', updateStickyTop, { passive: true });
    updateStickyTop();

    return () => {
      window.removeEventListener('scroll', updateStickyTop);
      window.removeEventListener('resize', updateStickyTop);
    };
  }, [stickyButtons, open]);

  const toggleRoot = (hide = true) => {
    const root = document.getElementById('root');
    if (root) {
      root.style.display = hide ? 'none' : 'block';
    }
    if (!hide) {
      setBodyOverflow();
    }
  };

  const theme = get(view, 'style.palette.mode', 'light');

  useEffect(() => {
    toggleRoot(open);
    if (open && theme === 'dark') {
      toggleTheme();
      setToggleBack(true);
    } else if (!open && toggleBack) {
      toggleTheme();
      setToggleBack(false);
    }
    if (!open && onClose) {
      onClose();
    } else if (open && onOpen) {
      onOpen();
    }
    if (!open) {
      setLoaded({});
    }
  }, [open]);

  // PFM-2690: WSOD on browser Back after printing. When the user triggers a print
  // and then presses the browser BACK button, PrintView unmounts on the route change
  // WITHOUT the [open]-effect ever running its close branch, so #root stays
  // display:none (white screen) and a dark theme stays toggled to light. Guarantee
  // every global DOM/theme mutation the open path makes is reverted on unmount.
  // Refs keep this empty-deps cleanup pointed at the latest values without re-running.
  const toggleBackRef = useRef(false);
  const toggleThemeRef = useRef(toggleTheme);
  toggleBackRef.current = toggleBack;
  toggleThemeRef.current = toggleTheme;
  useEffect(() => {
    return () => {
      const root = document.getElementById('root');
      if (root) {
        root.style.display = 'block';
      }
      // Restore body scroll/position locked by the body-overflow hook.
      setBodyOverflow();
      // Revert a theme flip the open path applied but never got to undo.
      if (toggleBackRef.current) {
        toggleThemeRef.current();
      }
    };
  }, []);

  const openTrigger = get(props, 'openTrigger');
  useEffect(() => {
    if (openTrigger) {
      setOpen(true);
    }
  }, [openTrigger]);

  const onPrint = () => {
    testNativeSelect().then(() => {
      if (open) {
        executePrint();
      } else {
        setOpen(true);
      }
    });
  };

  const onExport = async () => {
    try {
      setExp((s) => ({ ...s, loading: true }));
      const excelJsModule = import(/* webpackChunkName: "exceljs" */ 'exceljs');

      const _id = get(assessment, 'id');

      const scdata = await Promise.all(
        get(assessment, 'scorecards', []).map(
          async (scard) =>
            await Promise.all([
              sendData(
                { url: `/processed-scorecard-score-values` },
                {
                  normalize: { build: ['processedScorecardScoreValues'] },
                  params: {
                    filter: { assessment_id: _id, scorecard_id: get(scard, 'id') },
                    include: 'checklist,checkpoint,subScores,subScores.images,subScores.realm',
                  },
                }
              ),
              first(
                await sendData(
                  { url: `/scorecards/${scard.id}` },
                  {
                    normalize: { build: ['scorecards'] },
                    params: {
                      filter: { viewable: true, withInactive: true },
                      include: 'checklists,checklists.images,checklists.checkpoints', //,checklists.checkpoints.scorecardOptions',
                    },
                  }
                )
              ),
              sendData(
                { url: `/processed-scorecard-score-values` },
                {
                  normalize: { build: ['processedScorecardScoreValues'] },
                  params: {
                    filter: { assessment_id: _id, scorecard_id: get(scard, 'id'), status: 'goal' },
                  },
                }
              ),
            ])
        )
      );

      const getuid = (i) => {
        if (i.depth === 2 && !!i.scorecardScoreId) return;
        return `${i.scorecardId}_${i.systemId}_${i.supersetId}_${i.checklistId}_${i.checkpointId}`;
      };

      const hasNewTargets = (get(assessment, 'availableStatus', []) || []).includes('goal');

      const ExcelJS = (await excelJsModule).default;
      const book = new ExcelJS.Workbook();
      const sheet = book.addWorksheet('Sheet');

      const rowcount = hasNewTargets ? 9 : 8;
      const createRow = (arr = []) => {
        const ret = [];
        for (let i = 0; i < rowcount; i++) {
          ret.push(arr[i] !== undefined ? arr[i] : '');
        }
        return ret;
      };

      const itemSeperator = ' | ';

      const aname = assessment.name;

      const displayRows = [
        createRow([aname]),
        createRow(),
        createRow(['Criteria', scdata.map((s) => get(s, '1.name')).join(itemSeperator)]),
        createRow(['Location', getRealmName(assessment, '', true)]),
        createRow(['Author', get(assessment, 'authors.0.name')]),
        createRow(['Editors', assessment.editors.map((a) => a.name).join(itemSeperator)]),
        createRow(['Viewers', assessment.viewers.map((a) => a.name).join(itemSeperator)]),
        createRow(['Created At', moment(get(assessment, 'createdAt')).format('L')]),
        createRow(['Downloaded At', moment().format('L')]),
        createRow(),
      ];

      const tableProps = {
        name: 'MyTable',
        ref: 'A12',
        headerRow: true,
        style: {
          showRowStripes: true,
        },
        columns: [
          { name: 'Category', filterButton: true, width: 100 },
          { name: 'Checklist', filterButton: true, width: 100 },
          { name: 'Checkpoint', filterButton: true, width: 100 },
          { name: 'Description', filterButton: true, width: 100 },
          { name: 'Notes', filterButton: true, width: 100 },
          { name: 'Instance', filterButton: true, width: 100 },
          { name: 'Score', filterButton: true, width: 100 },
          ...(hasNewTargets ? [{ name: 'New Target', filterButton: true, width: 100 }] : []),
          { name: 'ID', filterButton: true, width: 100 },
        ],
        rows: [],
      };

      let currentcard;
      const subDisplayScore = (s) => {
        const map = {
          0: get(
            get(currentcard, 'config.subScoreValueLabel', []).find((f) => f.value == 0),
            'label',
            'FAIL'
          ),
          '-1': get(
            get(currentcard, 'config.subScoreValueLabel', []).find((f) => f.value == -1),
            'label',
            'NOTE'
          ),
          100: get(
            get(currentcard, 'config.subScoreValueLabel', []).find((f) => f.value == 100),
            'label',
            'PASS'
          ),
        };

        return get(map, parseInt(s.score), 'NOTE');
      };

      let targetMap = {};

      console.log({ scdata });

      const loopRows = (r, parents = []) => {
        const type = getItemType(r);
        const isgob = isGob(r);
        const id = (() => {
          const v = ['supersetId', 'checklist.classificationId', 'checkpoint.grandestId'].map((k) => get(r, k)).filter((f) => !!f);
          if (isgob) {
            v.push('0');
          }
          return v.join('-');
        })();

        const uid = getuid(r);
        const targetScore = uid ? get(targetMap, uid) : null;
        const targetDisplay = get(targetScore, 'displayValue', get(targetScore, 'displayScore', ''));

        if (type === 'superset') {
          tableProps.rows.push(createRow([r.name, 'OVERALL', '', '', '', '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id]));
        } else if (type === 'checklist') {
          tableProps.rows.push(createRow([get(parents, '0.name'), r.name, 'OVERALL', '', '', '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id]));
        } else if (isgob) {
          tableProps.rows.push(
            createRow([get(parents, '0.name'), get(parents, '1.name'), 'GENERAL OBSERVATIONS', '', get(r, 'criteria', ''), '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id])
          );
        } else if (type === 'checkpoint') {
          const subs = get(r, 'subScores', []);

          if (size(subs)) {
            tableProps.rows.push(
              createRow([get(parents, '0.name'), get(parents, '1.name'), r.name, get(r, 'criteria', ''), get(r, 'notes', ''), '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id])
            );
            subs.map((s) => {
              tableProps.rows.push(
                createRow([get(parents, '0.name'), get(parents, '1.name'), r.name, get(r, 'criteria', ''), get(s, 'notes', ''), 'yes', subDisplayScore(s), ...(hasNewTargets ? [''] : []), id])
              );
            });
          } else {
            tableProps.rows.push(
              createRow([get(parents, '0.name'), get(parents, '1.name'), r.name, get(r, 'criteria', ''), get(r, 'notes', ''), '', r.displayScore, ...(hasNewTargets ? [targetDisplay] : []), id])
            );
          }
        }

        if (r.children) {
          const childOrder =
            type === 'checklist'
              ? r.children.sort((a, b) => {
                  if (!a.checkpointId) {
                    return -1;
                  }
                  if (!b.checkpointId) {
                    return 1;
                  }
                  return a.name.localeCompare(b.name);
                })
              : orderBy(r.children, 'order,name');
          childOrder.map((c) => loopRows(c, [...parents, r]));
        }
      };

      scdata.map((sc) => {
        const [scores, scorecard, targets] = sc;
        const treed = arrayToTree(scores, { parentProperty: 'parentId' });

        // Build target score lookup by uid
        targetMap = {};
        (targets || []).forEach((t) => {
          const uid = getuid(t);
          if (uid) targetMap[uid] = t;
        });

        displayRows.push(createRow([scorecard.name]));
        displayRows.map((r) => sheet.addRow(r));

        currentcard = scorecard;

        treed.map((t) => {
          orderBy(t.children, 'order,name').map((c) => loopRows(c));
        });
      });

      [
        { id: 1, width: 30 },
        { id: 2, width: 40 },
        { id: 3, width: 20 },
        { id: 4, width: 50 },
        { id: 5, width: 50 },
        { id: 6, width: 10 },
        { id: 7, width: 10 },
        ...(hasNewTargets ? [{ id: 8, width: 12 }] : []),
        { id: hasNewTargets ? 9 : 8, width: 15 },
      ].map((r) => (sheet.getColumn(r.id).width = r.width));

      sheet.addTable(tableProps);

      const buffer = await book.xlsx.writeBuffer();
      saveWorksheet(`${new Date().toISOString()}_${aname}_export.xlsx`, buffer);
    } catch (err) {
      alert(err);
    } finally {
      setExp((s) => ({ ...s, loading: false }));
    }
  };

  const extraClick = (e) => {
    testNativeSelect().then(() => {
      get(button, 'onClick', () => 0)(e);
      onPrint(e);
    });
  };

  const onLoaded = (li, i) => {
    setLoaded((l) => ({ ...l, [i]: true }));
  };

  const sectionToggles = get(props, 'sectionToggles', []);
  const onToggleSection = get(props, 'onToggleSection');

  const renderButtons = () => {
    const printText = open ? 'Print' : get(props, 'printBtn.text', `Print ${size(items)}`);
    const printIcon = get(props, 'printBtn.icon');
    const printOnClick = get(props, 'printBtn.onClick');
    const exportBtn = get(props, 'exportBtn');
    const shareBtn = get(props, 'shareBtn');
    const printStyle = get(props, 'printBtn.style', {});
    const useLoaded = get(props, 'useLoaded', false);
    const loadComplete = Object.keys(loaded).length >= size(items);

    const stickyButtonsActive = stickyButtons && !open;
    const showWhenEmpty = get(props, 'showWhenEmpty', false);
    // PFM-2628 (rework): opt-in mobile placement for the action cluster.
    //  - 'hideOnMobile': hide the in-page Tabs cluster below `lg`.
    //  - 'headerRow': render inline with Location/Status, right-aligned, below
    //    `lg` (same breakpoint at which Author/Scorecards hide); hidden at `lg`+.
    //  - 'inFlow': always in-flow (document list inside FlexibleLayout).
    // Defaults to the unchanged desktop top-right overlay everywhere.
    const mobileVariant = get(props, 'mobileVariant');

    const printCountDisabled = !open && size(items) === 0;
    const disabled = !useLoaded ? printCountDisabled : open ? !loadComplete : printCountDisabled;

    const buttonContainerStyle = {
      ...(!open ? props.buttonContainerStyle : {}),
      ...(stickyButtonsActive ? { top: stickyTop } : {}),
    };

    // Below lg (when icons are provided): square MUI IconButtons. At lg+ / dialog: labeled Buttons.
    const useIconButtons = !open && isIconOnly && !!(printIcon || get(exportBtn, 'icon') || get(shareBtn, 'icon'));

    const shareControl =
      !!shareBtn &&
      !open &&
      (useIconButtons ? (
        <IconButton color="inherit" onClick={shareBtn.onClick} data-testid={shareBtn['data-testid']} aria-label={shareBtn.label || 'Share'}>
          {shareBtn.icon}
        </IconButton>
      ) : React.isValidElement(shareBtn) ? (
        shareBtn
      ) : (
        <Button onClick={shareBtn.onClick} data-testid={shareBtn['data-testid']} startIcon={shareBtn.icon}>
          {shareBtn.label}
        </Button>
      ));

    const exportControl =
      !!exportBtn &&
      !open &&
      (useIconButtons ? (
        <IconButton color="inherit" onClick={onExport} disabled={!!exportBtn.disabled || exp.loading} aria-label={exportBtn.text || 'Download Data'}>
          {exp.loading ? <CircularProgress size={24} color="inherit" /> : exportBtn.icon}
        </IconButton>
      ) : (
        <Button disabled={exportBtn.disabled} onClick={onExport} loading={exp.loading} style={get(exportBtn, 'style', {})}>
          {get(exportBtn, 'text')}
        </Button>
      ));

    const printControl = useIconButtons ? (
      <IconButton color="inherit" disabled={disabled} onClick={printOnClick || onPrint} aria-label={typeof printText === 'string' ? printText : 'Print'}>
        {printIcon}
      </IconButton>
    ) : (
      <Button style={open ? {} : printStyle} disabled={disabled} onClick={printOnClick && !open ? printOnClick : onPrint}>
        {printText}
      </Button>
    );

    return !size(items) && !size(sectionToggles) && !showWhenEmpty ? null : (
      <ButtonContainer
        style={buttonContainerStyle}
        className={clsx(open && 'fixed', stickyButtonsActive && 'sticky', !open && mobileVariant)}
      >
        {button && !open && <Button onClick={extraClick}>{get(button, 'title', 'Print All')}</Button>}
        {open && (
          <Button variant="text" onClick={() => setOpen(false)}>
            {'Close'}
          </Button>
        )}
        {shareControl}
        {exportControl}
        {printControl}
        {open && size(sectionToggles) > 0 && (
          <SectionToggles>
            {sectionToggles.map((toggle) => (
              <SectionToggleLabel
                key={toggle.name}
                control={<Checkbox checked={toggle.enabled} onChange={(e) => onToggleSection && onToggleSection(toggle.name, e.target.checked)} size="small" />}
                label={toggle.label}
              />
            ))}
          </SectionToggles>
        )}
      </ButtonContainer>
    );
  };

  const hideCoverPage = get(props, 'hideCoverPage', false);

  return (
    <>
      {!!open && (
        <StyledDialog fullScreen={true} open={open}>
          <FauxPage>
            {!hideCoverPage && (
              <div>
                <Title>{title}</Title>
                {items.map((item, i) => {
                  return <CoverTitle key={i}>{item.coverTitle}</CoverTitle>;
                })}
              </div>
            )}

            {items.map((li, i) => (
              <PrintRow key={i} first={i === 0 && hideCoverPage} li={li} pagebreak={pagebreak} onLoaded={() => onLoaded(li, i)} />
            ))}
          </FauxPage>

          <PrintCopyright>{getCopyrightText(props.copyrightHolder || copyrightHolder)}</PrintCopyright>

          {renderButtons()}
        </StyledDialog>
      )}

      {renderButtons()}
    </>
  );
};
